<template>
  <NuxtLink v-if="!video" class="app-community-card-item" :to="url" :target="isSameOriginUrl(url) ? '_self' : '_blank'">
    <AspectRatio class="overflow-hidden" :ratio="ratio">
      <img
        class="app-community-card-img w-full h-full object-cover transition-all duration-500"
        :src="image"
        :alt="title"
        loading="lazy"
      />
    </AspectRatio>
    <div class="app-community-card-ctx">
      <h4 class="app-community-card-title truncate ...">{{ title }}</h4>
      <div v-if="description" class="app-community-card-desc">{{ description }}</div>
    </div>
  </NuxtLink>
  <CommonVideoPopup v-else :url="url">
    <div class="app-community-card-item cursor-pointer">
      <AspectRatio class="overflow-hidden" :ratio="ratio">
        <img
          class="app-community-card-img w-full h-full object-cover transition-all duration-500"
          :src="image"
          :alt="title"
          loading="lazy"
        />
      </AspectRatio>
      <div class="app-community-card-ctx">
        <h4 class="app-community-card-title truncate ...">{{ title }}</h4>
        <div v-if="description" class="app-community-card-desc">{{ description }}</div>
      </div>
      <div class="app-community-card-play transition-colors duration-500">
        <IconsPlay class="w-full h-full max-lg:scale-[1.4]" />
      </div>
      <!--  -->
      <div v-show="videoTag" class="app-community-card-video-tag">{{ videoTag }}</div>
      <div class="app-community-card-like">
        <div class="app-community-card-like-icon" @click.stop="likeIconClick">
          <IconsLike :type="isLiked ? 'liked' : 'default'" />
        </div>
        <div class="app-community-card-like-nums" v-if="`${likeCount}` !== '0'">
          <span class="app-community-card-like-nums-text" :class="{ 'text-primary': isLiked }">{{ likeCount }}</span>
        </div>
      </div>
    </div>
  </CommonVideoPopup>
</template>

<script setup lang="ts">
import { isSameOriginUrl } from '~/utils'
const emit = defineEmits(['lickeIconClickEmit'])
defineProps({
  title: {
    type: String,
    required: true
  },
  image: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  url: {
    type: String,
    default: ''
  },
  videoTag: {
    type: String,
    default: ''
  },
  isLiked: {
    type: Boolean,
    default: false
  },
  likeCount: {
    type: String,
    default: '0'
  },
  video: Boolean
})
const ratio = 390 / 220
// Emit event 'lickeIconClickEmit' when like icon is clicked

function likeIconClick() {
  emit('lickeIconClickEmit')
}
</script>

<style scoped lang="scss">
.app-community-card-item {
  @apply relative block;
}
.app-community-card-ctx {
  @apply absolute bottom-0 left-0 right-0  flex flex-col w-full; // all
  @apply ltr:pl-4 rtl:pr-4;
  @apply py-[6px];
  @apply ltr:pr-[90px]  ltr:rtl:pr-4;
  @apply rtl:pl-[90px] rtl:ltr:pl-4;
  // Figma: linear-gradient(0deg, rgba(0,0,0,1) 16%, rgba(0,0,0,0) 100%)
  background: linear-gradient(0deg, rgba(0, 0, 0, 1) 16%, rgba(0, 0, 0, 0) 100%);
}
.app-community-card-title {
  @apply relative text-[20px]/[24px] font-medium text-white px-3;
  @apply before:content-[''] before:bg-primary-color before:w-1 before:h-4 before:absolute before:top-1  ltr:before:left-0 rtl:before:right-0;
}
.app-community-card-desc {
  @apply relative text-[#d1d1d1] text-[20px]/[24px] px-3 mt-1;
  @apply before:content-[''] before:bg-light before:w-1 before:h-1 before:absolute before:top-[10px];
  @apply ltr:before:left-0 rtl:before:right-0;
}
.app-community-card-play {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden;
  @apply w-11 h-11 bg-dark-background rounded-full flex items-center justify-center text-white;
}
@media not all and (hover: none) {
  .app-community-card-item:hover {
    .app-community-card-play {
      @apply text-primary;
    }
    .app-community-card-img {
      @apply scale-105;
    }
  }
}
.app-community-card-video-tag {
  @apply absolute top-2  text-white;
  @apply ltr:left-0;
  @apply rtl:right-0;
  @apply text-[20px]/[24px];
  @apply py-1;
  @apply ltr:pr-2 ltr:pl-4;
  @apply rtl:pl-2 rtl:pr-4;
  @apply bg-[#363A41];
  @apply before:content-[''] before:absolute before:bg-primary;
  @apply before:w-1 before:h-1;
  @apply before:top-1/2 before:translate-x-[-50%];
  @apply before:ltr:left-2 before:rtl:right-2;
}

.app-community-card-like {
  @apply absolute;
  @apply w-[68px] h-[52px];
  @apply ltr:right-1 bottom-1;
  @apply rtl:left-1;
}
.app-community-card-like-icon {
  @apply pointer-events-auto;
  @apply absolute ltr:left-0 rtl:right-0 bottom-0;
  @apply w-10 h-10;
  @apply hover:text-primary;
  @apply transition-colors duration-500;
  svg {
    @apply w-full h-full;
  }
}
.app-community-card-like-nums {
  @apply pointer-events-none;
  @apply absolute top-[2px] ltr:left-[16px] px-1;
  @apply rtl:right-[16px];
  @apply text-[#D1D1D1];
  @apply hover:text-primary;
  @apply text-[18px]/[22px];
  @apply leading-[22px];
  @apply transition-colors duration-500;
  @apply bg-[rgba(20,20,20,0.7)] rounded-[12px];
  @apply min-w-[30px];
  @apply text-center;
  // @apply before:absolute before:w-full before:h-6 before:left-0 before:top-1/2 before:-translate-y-1/2;
  // @apply before:bg-[rgba(20,20,20,0.7)] before:rounded-[8px];
  // @apply before:z-10;;;;;;;;;;;
}
.app-community-card-like-nums-text {
  @apply relative z-20;
}
</style>
